import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  Film,
  Languages,
  LogOut,
  Moon,
  Search,
  ShieldCheck,
  Sun,
  Upload,
  User as UserIcon,
  Video,
} from "lucide-react";
import { useI18n } from "../i18n";
import { useTheme } from "../app/theme";
import { useAuth } from "../app/auth";
import { Avatar, Button, IconButton } from "./ui";
import { SearchDialog } from "../features/feed/SearchDialog";
import type { TranslationKey } from "../i18n/en";

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: typeof Film;
  admin?: boolean;
  auth?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", labelKey: "nav.feed", icon: Film },
  { to: "/upload", labelKey: "nav.upload", icon: Upload, auth: true },
  { to: "/mine", labelKey: "nav.mine", icon: Video, auth: true },
  { to: "/admin", labelKey: "nav.admin", icon: ShieldCheck, admin: true },
];

function BrandMark() {
  return (
    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-linear-to-br from-brand-400 to-accent-500 text-white shadow-glow">
      <Film className="size-5" aria-hidden />
    </span>
  );
}

export function AppShell() {
  const { t, lang, toggleLang } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const items = NAV_ITEMS.filter(
    (item) => (!item.admin || user?.is_admin) && (!item.auth || user),
  );

  const onLogout = async () => {
    setBusy(true);
    try {
      await logout();
    } finally {
      setBusy(false);
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition",
      isActive
        ? "bg-brand-500/15 text-brand-200 ring-1 ring-brand-500/30"
        : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
    );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[17rem_1fr]">
      {/* Desktop sidebar. Logical properties keep the border on the correct side in RTL. */}
      <aside className="sticky top-0 hidden h-dvh flex-col gap-6 border-e border-[var(--color-hairline)] bg-[var(--color-surface)]/40 p-5 lg:flex">
        <Link to="/" className="flex items-center gap-3">
          <BrandMark />
          <span className="flex flex-col leading-tight">
            <span className="text-base font-semibold">{t("app.name")}</span>
            <span className="text-xs text-[var(--color-ink-faint)]">{t("app.tagline")}</span>
          </span>
        </Link>

        <nav className="flex flex-1 flex-col gap-1" aria-label={t("nav.menu")}>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
          >
            <Search className="size-4.5 shrink-0" aria-hidden />
            <span>{t("search.title")}</span>
          </button>
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={navLinkClass}>
              <item.icon className="size-4.5 shrink-0" aria-hidden />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="space-y-3 border-t border-[var(--color-hairline)] pt-4">
          <div className="flex items-center gap-1">
            <IconButton label={t("common.language")} onClick={toggleLang}>
              <span className="flex items-center gap-1 text-xs font-semibold">
                <Languages className="size-4" aria-hidden />
                {lang === "ar" ? "EN" : "ع"}
              </span>
            </IconButton>
            <IconButton label={t("common.theme")} onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="size-4.5" aria-hidden /> : <Moon className="size-4.5" aria-hidden />}
            </IconButton>
          </div>

          {user ? (
            <div className="space-y-2">
              <Link
                to={"/u/" + user.username}
                className="flex items-center gap-3 rounded-xl p-2 transition hover:bg-[var(--color-surface-2)]"
              >
                <Avatar username={user.username} url={user.avatar_url} size={36} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{user.username}</span>
                  <span className="block truncate text-xs text-[var(--color-ink-faint)]">
                    {user.email}
                  </span>
                </span>
              </Link>
              <Button variant="ghost" size="sm" block loading={busy} onClick={onLogout}>
                <LogOut className="size-4" aria-hidden />
                {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <div className="grid gap-2">
              <Link
                to="/login"
                state={{ from: location.pathname }}
                className="grid h-9 place-items-center rounded-xl bg-linear-to-b from-brand-400 to-brand-600 text-sm font-medium text-white shadow-lift transition hover:from-brand-300 hover:to-brand-500"
              >
                {t("nav.login")}
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar. */}
      <header className="glass sticky top-0 z-50 flex items-center justify-between gap-2 px-4 py-3 lg:hidden">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark />
          <span className="text-sm font-semibold">{t("app.name")}</span>
        </Link>
        <div className="flex items-center gap-1">
          <IconButton label={t("search.title")} onClick={() => setSearchOpen(true)} className="size-9">
            <Search className="size-4.5" aria-hidden />
          </IconButton>
          <IconButton label={t("common.language")} onClick={toggleLang} className="size-9">
            <span className="text-xs font-semibold">{lang === "ar" ? "EN" : "ع"}</span>
          </IconButton>
          <IconButton label={t("common.theme")} onClick={toggleTheme} className="size-9">
            {theme === "dark" ? <Sun className="size-4.5" aria-hidden /> : <Moon className="size-4.5" aria-hidden />}
          </IconButton>
          {user ? (
            <div className="flex items-center gap-1.5">
              <Link to={"/u/" + user.username} aria-label={t("nav.profile")}>
                <Avatar username={user.username} url={user.avatar_url} size={32} />
              </Link>
              <IconButton
                label={t("nav.logout")}
                onClick={onLogout}
                disabled={busy}
                className="size-9 text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300"
              >
                <LogOut className="size-4.5" aria-hidden />
              </IconButton>
            </div>
          ) : (
            <Link
              to="/login"
              className="rounded-xl bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
            >
              {t("nav.login")}
            </Link>
          )}
        </div>
      </header>

      <main className="min-w-0 pb-20 lg:pb-0">
        <Outlet />
      </main>

      {/* Mobile tab bar. */}
      <nav
        className="glass fixed inset-x-0 bottom-0 z-50 flex items-stretch justify-around px-2 pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label={t("nav.menu")}
      >
        {(items.length ? items : NAV_ITEMS.slice(0, 1)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              clsx(
                "flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition",
                isActive ? "text-brand-300" : "text-[var(--color-ink-faint)]",
              )
            }
          >
            <item.icon className="size-5" aria-hidden />
            {t(item.labelKey)}
          </NavLink>
        ))}
        {!user && (
          <Link
            to="/login"
            className="flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-[var(--color-ink-faint)]"
          >
            <UserIcon className="size-5" aria-hidden />
            {t("nav.login")}
          </Link>
        )}
      </nav>

      {/* Global Search Dialog */}
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
